<script lang="ts" setup>
import { useProjectStore } from '@/store/projectStore';
import type { SideNavigationItemProps } from './AppSideNavigationItem.vue';

type SideNavigationButton = {
  id: string;
  shouldRender: boolean;
} & SideNavigationItemProps;

interface Props {
  fullMode: boolean;
  projectId: string;
  isMobile?: boolean;
}
const props = defineProps<Props>();

const projectStore = useProjectStore();

const mode = computed(() => (props.fullMode ? 'full' : 'minimal'));

const project = computed(() => projectStore.project(props.projectId));
const projectNavigationItems = computed((): SideNavigationButton[] => {
  return [
    {
      id: 'side-nav-opened-project',
      to: {
        name: 'ProjectPage',
        params: { projectId: props.projectId },
      },
      icon: 'Map',
      mode: mode.value,
      label: project.value.title,
      // Pages that hang off the map: entity pages and the map header's links
      activeRoutes: [
        'ProjectActivity',
        'ProjectMilestoneRootPage',
        'ProjectSearchResults',
        'ProjectVolatilityAnalytics',
        'ProjectSettingsChecklistRows',
      ],
      shouldRender: !props.isMobile,
    },
    {
      id: 'side-nav-project-overview',
      to: {
        name: 'ProjectOverview',
        params: { projectId: props.projectId },
      },
      icon: 'ChartPie',
      mode: mode.value,
      label: $t('sideNavigation.projectOverview'),
      activeRoutes: ['ProjectActivities', 'ProjectMilestones'],
      shouldRender: true,
    },
    {
      id: 'side-nav-project-list',
      to: {
        name: 'ProjectListView',
        params: { projectId: props.projectId },
      },
      icon: 'List',
      mode: mode.value,
      label: $t('sideNavigation.listView'),
      activeRoutes: ['ProjectKanbanView'],
      shouldRender: true,
    },
    // The Kanban board is not a standalone nav item: it is reached via the
    // List/Board view switcher in the list/board page header (ProjectViewSwitcher),
    // since List and Board are two layouts of the same items.
    {
      id: 'side-nav-project-functions',
      to: {
        name: 'ProjectSettingsFunctions',
        params: { projectId: props.projectId },
      },
      icon: 'Users',
      mode: mode.value,
      label: $t('sideNavigation.functionsAndUsers'),
      shouldRender: !props.isMobile,
    },
    {
      id: 'side-nav-project-members',
      to: {
        name: 'ProjectSettingsMembers',
        params: { projectId: props.projectId },
      },
      icon: 'UserCog',
      mode: mode.value,
      label: $t('sideNavigation.projectMembers'),
      shouldRender: !props.isMobile,
    },
    {
      id: 'side-nav-project-documents',
      to: {
        name: 'ProjectSettingsDocuments',
        params: { projectId: props.projectId },
      },
      icon: 'File',
      mode: mode.value,
      label: $t('sideNavigation.projectDocuments'),
      shouldRender: !props.isMobile,
    },
    {
      id: 'side-nav-project-links-and-tools',
      to: {
        name: 'ProjectLinksAndTools',
        params: { projectId: props.projectId },
      },
      icon: 'Link',
      mode: mode.value,
      label: $t('sideNavigation.linksAndTools'),
      shouldRender: !props.isMobile,
    },
    {
      id: 'side-nav-project-meetings',
      to: {
        name: 'ProjectMeetings',
        params: { projectId: props.projectId },
      },
      icon: 'Laptop',
      mode: mode.value,
      label: $t('sideNavigation.meetings'),
      shouldRender: true,
    },
    {
      id: 'side-nav-project-history',
      to: {
        name: 'ProjectHistory',
        params: { projectId: props.projectId },
      },
      icon: 'History',
      mode: mode.value,
      label: $t('sideNavigation.history'),
      shouldRender: !props.isMobile,
    },
  ];
});
</script>

<template>
  <template v-for="item in projectNavigationItems" :key="item.id">
    <AppSideNavigationItem
      v-if="item.shouldRender"
      :id="item.id"
      :to="item.to"
      :icon="item.icon"
      :mode="item.mode"
      :label="item.label"
      :active-routes="item.activeRoutes"
    />
  </template>
</template>
